import React, {
  useState,
  type Dispatch,
  type SetStateAction,
  useMemo,
  useEffect,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";

import DeleteIcon from "@material-ui/icons/Delete";
import SyncIcon from "@material-ui/icons/Sync";

import {
  userPlanSelector,
  userGetInventoryTypeSelector,
} from "src/store/system/selector";
import { getPlaidIntegrations } from "src/store/plaidIntegration/selector";
import { USER_PLAN as UserPlan } from "src/enums/common.enum";
import {
  deleteIntegration as plaidDeleteIntegration,
  syncAccount as plaidSyncAccount,
} from "src/apiService/modules/plaid";
import {
  eBayIntegrationSyncChange,
  ebaySyncApi,
} from "src/apiService/modules/ebay";
import {
  getPoshmarkIntegration,
  poshmarkIntegrationCreate,
  poshmarkIntegrationSyncChange,
  poshmarkIntegrationDelete,
} from "src/apiService/modules/poshmark";
import { setActiveDialog } from "src/store/adminHtml/actions";
import PlaidAddIntegrationDialog from "src/components/PlaidAddIntegrationDialog";
import { StatefulLoadableIconButton } from "src/components/LoadableIconButton";
import {
  getEBayIntegrations,
  getPoshmarkIntegrations,
} from "src/store/plaidIntegration/selector";
import DeleteIntegrationDialog from "src/pages/GetStarted/DeleteIntegrationDialog";
import AddNewIntegrationDialog from "src/pages/GetStarted/AddNewIntegrationDialog";
import ImportDialog from "src/pages/GetStarted/ImportDialog";
import ResumeOrStartImportDialog from "./ResumeOrStartImportDialog";
import AddPoshmarkExtensionDialog from "src/components/AddPoshmarkExtensionDialog";
import PoshmarkConfirmDialog from "src/components/PoshmarkConfirmDialog";
import AddAmazonIntegrationDialog from "./AmazonRows/AddAmazonIntegrationDialog";
import AddEtsyIntegrationDialog from "./EtsyRows/AddEtsyIntegrationDialog";

import { MissingExtensionError } from "src/utils/extension";
import { getPoshmarkUser } from "src/utils/extension/poshmark";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import {
  getTourCurrentStep,
  getTourOpen,
  getTourStepType,
} from "src/store/tour/selector";
import {
  poshmarkSyncWithCheck,
  poshmarkSyncInventory,
  poshmarkSyncSales,
} from "src/store/integrationSync/actions/poshmark";
import { IsRunningThreshold as PoshmarkIsRunningThreshold } from "src/store/integrationSync/actions/utils";
import { usePreventImportPoshmark } from "src/pages/GetStarted/usePreventImportPoshmark";

import MonthYear from "src/interfaces/monthYear.interface";
import type {
  PoshmarkIntegration,
  EBayIntegration,
} from "src/interfaces/plaidIntegration.interface";
import { userRoleSelector } from "src/store/system/selector";
import { bankAccountByIntegrationIdSelector } from "src/store/bankAccount/selector";

import type { DialogState } from "./types";
import PlatformIntegrationsTableRow from "./PlatformIntegrationsTableRow";
import useIsRunning from "./useIsRunning";
import MercariRows from "./MercariRows";
import EtsyRows from "./EtsyRows";
import AmazonRows from "./AmazonRows";
import WhatnotRows from "./WhatnotRows";
import VintedRows from "./VintedRows";
import DepopRows from "./DepopRows";
import { DepopNotice } from "./DepopRows/Row";
import { VintedNotice } from "./VintedRows/Row";

const useDeleteButtonStyles = makeStyles((theme) => ({
  errorIconButton: {
    color: theme.palette.error.main,
    "&:hover, &.MuiIconButton-root:hover": {
      backgroundColor: "transparent !important",
    },
    "& svg": {
      transition: "transform 0.2s ease",
    },
    "&:hover svg": {
      transform: "scale(1.25)",
    },
  },
}));

// vim-highligh-fix = }

function PoshmarkIntegrationTableRow({
  integration,
  setDialog,
  hideInventory,
}: {
  integration: PoshmarkIntegration;
  setDialog: Dispatch<SetStateAction<DialogState>>;
  hideInventory: boolean;
}) {
  const dispatch = useDispatch();
  const isRunning = useIsRunning(
    integration.salesIsRunning,
    integration.inventoryIsRunning,
    PoshmarkIsRunningThreshold
  );
  const bankAccount = useSelector((state) =>
    integration?.id
      ? bankAccountByIntegrationIdSelector(state, integration.id)
      : undefined
  );
  const AddComponent = useMemo(() => {
    return () => (
      <Button
        color="primary"
        variant="contained"
        onClick={() => dispatch(setActiveDialog("poshmark_update_shipping"))}
      >
        Update Shipping
      </Button>
    );
  }, [dispatch]);

  return (
    <PlatformIntegrationsTableRow
      key={integration.id}
      name={bankAccount?.name || "Poshmark"}
      platform="Poshmark"
      isRunning={isRunning}
      sync={integration.sync}
      inventory={integration.sync && integration.inventory !== false}
      onPullDataNow={async () => {
        await dispatch(poshmarkSyncWithCheck(integration.id, true));
      }}
      onImport={() => {
        setDialog({
          type: "import",
          props: {
            integrationId: integration.id,
            fetchListings: (integrationId) =>
              dispatch(poshmarkSyncInventory(integrationId)),
            fetchTransactions: async (
              integrationId: string,
              start: MonthYear,
              end: MonthYear
            ) => dispatch(poshmarkSyncSales(integrationId, { start, end })),
            providerName: "Poshmark",
            usePreventImport: usePreventImportPoshmark,
          },
        });
      }}
      onSyncChanged={async (sync, inventory) => {
        await poshmarkIntegrationSyncChange(integration.id, sync, inventory);
        if ((inventory === undefined || inventory) && sync)
          await dispatch(poshmarkSyncWithCheck(integration.id, true));
      }}
      hideInventory={hideInventory}
      onDelete={() =>
        setDialog({
          type: "delete",
          props: {
            integration,
            deleteIntegration: poshmarkIntegrationDelete,
          },
        })
      }
      AddComponent={AddComponent}
    />
  );
}

function EBayIntegrationTableRow({
  integration,
  setDialog,
  hideInventory,
}: {
  integration: EBayIntegration;
  setDialog: Dispatch<SetStateAction<DialogState>>;
  hideInventory: boolean;
}) {
  const dispatch = useDispatch();
  const bankAccount = useSelector((state) =>
    integration?.id
      ? bankAccountByIntegrationIdSelector(state, integration.id)
      : undefined
  );
  const isRunning = !!(
    integration.salesIsRunning || integration.inventoryIsRunning
  );

  return (
    <PlatformIntegrationsTableRow
      key={integration.id}
      name={bankAccount?.name || integration.name || "eBay"}
      platform="eBay"
      isRunning={isRunning}
      sync={integration.sync}
      inventory={integration.sync && integration.inventory !== false}
      showReconnectAction={!!integration.error}
      onOpenNotifications={() => dispatch(setActiveDialog("notifications"))}
      onImport={() => {
        setDialog({
          type: "import",
          props: { integrationId: integration.id },
        });
      }}
      onSyncChanged={async (sync, inventory) => {
        await eBayIntegrationSyncChange(integration.id, sync, inventory);
        if ((inventory === undefined || inventory) && sync)
          ebaySyncApi(integration.id);
      }}
      hideInventory={hideInventory}
      onPullDataNow={async () => {
        await ebaySyncApi(integration.id);
        toast.success("Request received. Your imports are processing.");
      }}
      onAdd={() => setDialog({ type: "ebay-add" })}
      onDelete={() =>
        setDialog({
          type: "delete",
          props: { integration },
        })
      }
    />
  );
}

function PlatformIntegrationsTable() {
  const recordEvent = useRecordEvent();

  const hideInventory = useSelector(
    (s: any) => userGetInventoryTypeSelector(s) === "cash"
  );
  const ebayIntegrations = useSelector(getEBayIntegrations);
  const poshmarkIntegrations = useSelector(getPoshmarkIntegrations);
  const [dialog, setDialog] = useState<DialogState>({});

  const handlePoshmarkAdd = async () => {
    try {
      const data = await getPoshmarkUser();
      setDialog({
        type: "poshmark-confirm",
        props: {
          username: data.username,
          onConfirm: async (bankAccountId?: string | undefined) => {
            if (data?.cookies?.length) {
              const existingPoshmarkIntegration =
                await getPoshmarkIntegration();
              if (existingPoshmarkIntegration?.id) {
                toast.info("That Poshmark account is already connected.");
                return;
              }
              await poshmarkIntegrationCreate({
                cookies: data.cookies,
                username: data.username,
                bankAccountId,
              });
              recordEvent({
                type: "feature-adoption",
                feature: "integration-added",
                element: "sale-platform-poshmark",
              });
            }
            setDialog({});
          },
        },
      });
    } catch (e) {
      if (e instanceof MissingExtensionError) {
        setDialog({ type: "poshmark-add-extension" });
        return;
      }
      toast.error((e as Error).message);
    }
  };

  return (
    <>
      <AddNewIntegrationDialog
        open={dialog.type === "ebay-add"}
        onClose={() => setDialog({})}
      />
      <DeleteIntegrationDialog
        {...(dialog.type === "delete" ? dialog.props : {})}
        onClose={() => setDialog({})}
      />
      <ImportDialog
        {...(dialog.type === "import" ? dialog.props : {})}
        onClose={() => setDialog({})}
      />
      <ResumeOrStartImportDialog
        open={dialog.type === "resume-or-import"}
        {...(dialog.type === "resume-or-import" ? dialog.props : {})}
        onClose={() => setDialog({})}
        onStartOver={(importProps) =>
          setDialog({ type: "import", props: importProps })
        }
      />
      <AddPoshmarkExtensionDialog
        open={
          dialog.type === "poshmark-add-extension" ||
          dialog.type === "mercari-add-extension" ||
          dialog.type === "whatnot-add-extension" ||
          dialog.type === "vinted-add-extension" ||
          dialog.type === "depop-add-extension"
        }
        {...dialog.props}
        onClose={() => setDialog({})}
      />
      <PoshmarkConfirmDialog
        {...(dialog.type === "poshmark-confirm" ||
        dialog.type === "mercari-confirm" ||
        dialog.type === "whatnot-confirm" ||
        dialog.type === "vinted-confirm" ||
        dialog.type === "depop-confirm"
          ? dialog.props
          : {})}
        onCancel={() => setDialog({})}
      />
      <AddAmazonIntegrationDialog
        open={dialog.type === "amazon-add"}
        onClose={() => setDialog({})}
      />
      <AddEtsyIntegrationDialog
        open={dialog.type === "etsy-add"}
        onClose={() => setDialog({})}
      />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Sale Platform</TableCell>
            <TableCell>Platform</TableCell>
            <TableCell align="center">Import Past Data</TableCell>
            <TableCell align="center">
              Daily Integration - Sales and Fees
            </TableCell>
            {hideInventory ? null : (
              <TableCell align="center">
                Daily Integration - Inventory
              </TableCell>
            )}
            <TableCell align="center">Pull Data Now</TableCell>
            <TableCell align="center">Add</TableCell>
            <TableCell align="center">Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ebayIntegrations.length === 0 ? (
            <PlatformIntegrationsTableRow
              name="eBay"
              platform="eBay"
              isRunning={false}
              sync={false}
              inventory={false}
              onImport={() => {
                setDialog({ type: "ebay-add" });
              }}
              hideInventory={hideInventory}
              onSyncChanged={() => {
                setDialog({ type: "ebay-add" });
              }}
              onPullDataNow={async () => {
                setDialog({ type: "ebay-add" });
              }}
              onAdd={() => {
                setDialog({ type: "ebay-add" });
              }}
            />
          ) : (
            ebayIntegrations.map((integration) => {
              return (
                <EBayIntegrationTableRow
                  key={integration.id}
                  integration={integration}
                  setDialog={setDialog}
                  hideInventory={hideInventory}
                />
              );
            })
          )}
          {poshmarkIntegrations.length === 0 ? (
            <PlatformIntegrationsTableRow
              name="Poshmark"
              platform="Poshmark"
              isRunning={false}
              sync={false}
              inventory={false}
              onImport={handlePoshmarkAdd}
              hideInventory={hideInventory}
              onPullDataNow={async () => handlePoshmarkAdd()}
              onSyncChanged={handlePoshmarkAdd}
              onAdd={handlePoshmarkAdd}
            />
          ) : (
            poshmarkIntegrations.map((integration) => (
              <PoshmarkIntegrationTableRow
                key={integration.id}
                integration={integration}
                hideInventory={hideInventory}
                setDialog={setDialog}
              />
            ))
          )}
          <MercariRows hideInventory={hideInventory} setDialog={setDialog} />
          <EtsyRows hideInventory={hideInventory} setDialog={setDialog} />
          <AmazonRows hideInventory={hideInventory} setDialog={setDialog} />
          <WhatnotRows hideInventory={hideInventory} setDialog={setDialog} />
          <VintedRows hideInventory={hideInventory} setDialog={setDialog} />
          <DepopRows hideInventory={hideInventory} setDialog={setDialog} />
        </TableBody>
      </Table>
    </>
  );
}

function BankIntegrationsTableRow({
  plaidIntegration,
  userRole,
}: {
  plaidIntegration: ReturnType<typeof getPlaidIntegrations>[number];
  userRole: ReturnType<typeof userRoleSelector>;
}) {
  const classes = useDeleteButtonStyles();
  const bankAccount = useSelector((state) =>
    plaidIntegration?.id
      ? bankAccountByIntegrationIdSelector(state, plaidIntegration.id)
      : undefined
  );
  return (
    <TableRow>
      <TableCell>
        {bankAccount?.name || plaidIntegration.account_name}
      </TableCell>
      <TableCell align="center">
        <StatefulLoadableIconButton
          onClick={async () => {
            const results = await plaidSyncAccount(plaidIntegration.id);
            if (results.success) {
              toast.success("Request received. Your imports are processing.");
            } else {
              toast.error("Account Syncing Errored.");
            }
          }}
          color="primary"
        >
          <SyncIcon />
        </StatefulLoadableIconButton>
      </TableCell>
      <TableCell align="center">
        <StatefulLoadableIconButton
          disabled={userRole === "viewer"}
          className={classes.errorIconButton}
          onClick={async () =>
            await plaidDeleteIntegration(plaidIntegration.id)
          }
        >
          <DeleteIcon />
        </StatefulLoadableIconButton>
      </TableCell>
    </TableRow>
  );
}

function BankIntegrationsTable() {
  const plan = useSelector(userPlanSelector) || UserPlan.BASIC;
  const isBasic = plan === UserPlan.BASIC;
  const plaidIntegrations = useSelector(getPlaidIntegrations);
  const [open, setOpen] = useState(false);
  const userRole = useSelector(userRoleSelector);
  const tourStep = useSelector(getTourStepType);
  const currentStep = useSelector(getTourCurrentStep);
  const isOpen = useSelector(getTourOpen);

  useEffect(() => {
    if (tourStep === "connect-bank" && !isOpen) {
      if (currentStep === 1) {
        setOpen(true);
      }
    }
  }, [tourStep, currentStep, isOpen]);

  return (
    <>
      <PlaidAddIntegrationDialog open={open} onClose={() => setOpen(false)} />
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Bank Account / Credit Card</TableCell>
            <TableCell align="center">Pull Data Now</TableCell>
            <TableCell align="center">Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isBasic ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                As a Basic user, you can’t connect a bank or credit card to your
                account. If you’d like this functionality, you can upgrade by
                clicking the “Upgrade” button in the bottom left of the screen.
              </TableCell>
            </TableRow>
          ) : plaidIntegrations.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center">
                No bank integrations.
              </TableCell>
            </TableRow>
          ) : (
            plaidIntegrations.map((plaidIntegration) => {
              return (
                <BankIntegrationsTableRow
                  key={`plaid-${plaidIntegration.id}`}
                  plaidIntegration={plaidIntegration}
                  userRole={userRole}
                />
              );
            })
          )}
        </TableBody>
      </Table>
      {isBasic ? null : (
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen(true)}
            disabled={userRole === "viewer"}
            id="connect-bank-account-button"
          >
            Connect Bank Account/Credit Card
          </Button>
        </Box>
      )}
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    padding: theme.spacing(2, 2),
    marginTop: theme.spacing(2),
  },
  separator: {
    marginTop: theme.spacing(4),
  },
}));

function Content() {
  const classes = useStyles();

  return (
    <Paper className={classes.root}>
      <Typography variant="h5">Platform Integrations</Typography>
      <div id="platform-integrations-table">
        <PlatformIntegrationsTable />
      </div>
      <Box mt={2}>
        <Typography display="block" variant="caption">
          * {VintedNotice}
        </Typography>
        <Typography display="block" variant="caption">
          * {DepopNotice}
        </Typography>
      </Box>
      <Typography variant="h5" className={classes.separator}>
        Bank Integrations
      </Typography>
      <BankIntegrationsTable />
    </Paper>
  );
}

export default Content;
